import React from 'react';
import { Link } from 'react-router-dom';
import RiskBadge from './RiskBadge';
import StatusPill from './StatusPill';
import { Button } from './ui/button';
import { ArrowRight, ExternalLink, X, CalendarClock, ShieldCheck, Check } from 'lucide-react';

const TYPE_CHIP = { 'New patient': 'pill-indigo', 'Follow-up': 'pill-slate', Procedure: 'pill-violet', Surgery: 'pill-amber' };

const fmtWhen = (iso) =>
  iso ? new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

// One affected appointment: who, when, and the before → after risk impact of
// the status change. The risk pair is highlighted when the level actually moved.
function Row({ a }) {
  const before = a.before;
  const changed = before && before.level !== a.risk?.level;
  return (
    <div className={`dh-card p-3.5 ${a.isPast ? 'opacity-70' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 truncate">{a.patientName}</span>
            <span className={`pill ${TYPE_CHIP[a.visitType] || 'pill-slate'}`}>{a.visitType}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {fmtWhen(a.startTime)}{a.providerName ? ` · ${a.providerName}` : ''}{a.reason ? ` · ${a.reason}` : ''}
          </div>
        </div>
        <Link
          to={`/app/appointments/${a.id}`}
          className="text-xs text-brand-600 font-medium hover:underline inline-flex items-center gap-1 shrink-0 mt-0.5"
        >
          Open<ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      <div className="flex items-center gap-2 mt-2.5 flex-wrap">
        <StatusPill status={a.networkStatus} />
        <span className="ml-auto inline-flex items-center gap-1.5">
          {changed ? (
            <>
              <RiskBadge level={before.level} score={before.score} showScore />
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              <RiskBadge level={a.risk?.level} score={a.risk?.score} showScore />
            </>
          ) : (
            <RiskBadge level={a.risk?.level} score={a.risk?.score} showScore />
          )}
        </span>
      </div>
    </div>
  );
}

// A labelled group of affected appointments (Upcoming / Past).
function Section({ title, rows }) {
  if (!rows.length) return null;
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 px-0.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{title}</span>
        <span className="text-[11px] font-medium text-slate-400 tnum">{rows.length}</span>
      </div>
      {rows.map((a) => <Row key={a.id} a={a} />)}
    </div>
  );
}

// Right side-peek listing every appointment a plan's network-status change
// touches, split into upcoming vs. past with the before → after risk impact.
// Opened from the Network page; the change is confirmed via Approve.
export default function AffectedAppointmentsDrawer({ data, onClose, onApprove, onOpenWorklist }) {
  if (!data) return null;
  const { plan, fromStatus, toStatus, rows = [], changed = 0 } = data;
  const upcomingRows = rows.filter((a) => !a.isPast);
  const pastRows = rows.filter((a) => a.isPast);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-[1px]" />
      <aside className="dh-drawer-panel relative w-full max-w-[460px] h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-slate-100 shrink-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Network status changed</div>
              <h2 className="text-lg font-semibold text-slate-900 mt-0.5">
                {rows.length} appointment{rows.length === 1 ? '' : 's'} affected
              </h2>
            </div>
            <button onClick={onClose} className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
          </div>
          <div className="text-sm text-slate-600 mt-1.5">{plan?.payerName} · {plan?.planName}</div>
          <div className="flex items-center gap-2 mt-2">
            {fromStatus && <StatusPill status={fromStatus} />}
            {fromStatus && <ArrowRight className="h-3.5 w-3.5 text-slate-400" />}
            <StatusPill status={toStatus} />
          </div>
          <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5 text-slate-400" />{upcomingRows.length} upcoming</span>
            {changed > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <ShieldCheck className="h-3.5 w-3.5" />{changed} changed risk level
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {rows.length === 0 ? (
            <div className="dh-inset p-4 text-sm text-slate-500">No appointments currently resolve to this plan.</div>
          ) : (
            <>
              <Section title="Upcoming" rows={upcomingRows} />
              <Section title="Past" rows={pastRows} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 p-3 flex items-center justify-between shrink-0">
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <div className="flex items-center gap-2">
            {onOpenWorklist && rows.length > 0 && (
              <Button size="sm" variant="ghost" onClick={onOpenWorklist}>Open worklist</Button>
            )}
            <Button size="sm" className="bg-brand-600 hover:bg-brand-700 text-white" onClick={onApprove || onClose}>
              <Check className="h-3.5 w-3.5 mr-1" />Approve
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}
